// @flow
import type { Dispatch } from 'redux';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { getProductIdentifier, getProductTypeById } from '@finect/front-resources/Products';
import { setError } from './../error/actions';
import { fetchSetting } from './../settings/actions';
import { models } from './../../utils/models';

const expands = {
  collectives: `classes,commercializations,stats/performance,stats/standardDeviation,stats/alpha,stats/beta,stats/standardDeviation,stats/maxDrawdown,breakdown,stats/sharpeRatio,stats/correlation,stats/r2,stats/trackingError,stats/informationRatio,comparer`,
  xray: `classes,stats/performance,breakdown,portfolio,stats/beta,stats/trackingError,stats/maxDrawdown,stats/sharpeRatio,stats/informationRatio,stats/standardDeviation,stats/alpha`,
  generics: null
};

export function addFund(fund: Object) {
  return {
    type: 'COMPARISON_ADD_FUND',
    fund
  };
}

export function clearFunds() {
  return {
    type: 'COMPARISON_CLEAR_FUNDS'
  };
}

export function getFund(productType: Object, isin: string, storeInRedux: boolean = true) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path: `products/collectives/${productType.api}/${isin}?expand=${expands.collectives}`,
      method: 'GET',
      onSuccess: ({ data }: { data: Object }) => {
        const product = models.investmentProducts.product(productType, data);
        if (storeInRedux) dispatch(addFund(product));
        return product;
      },
      onError: () => {
        dispatch(setError({ code: 404, message: 'Fondo no encontrado' }));
        return null;
      }
    });
}

export function setFunds(funds: Array<Object>) {
  return {
    type: 'COMPARISON_SET_FUNDS',
    funds
  };
}

export function fetchFunds(productType: Object | null, funds: Array<string>, expand: string = 'collectives') {
  return (dispatch: Dispatch) =>
    Promise.all(
      funds.map(isin =>
        finectAPI({
          version: 'v4',
          path: `products/collectives/${productType ? productType.api : 'funds'}/${isin}${
            productType ? `?expand=${expands[expand]}` : ''
          }`,
          method: 'GET',
          onSuccess: ({ data }: { data: Object }) => {
            if (productType) {
              return models.investmentProducts.product(productType, data);
            }

            // fetch funds with id en path 'products/ES0114678034'
            return finectAPI({
              version: 'v4',
              path: `products/${data.id}?expand=${expands[expand]}`,
              method: 'GET',
              onSuccess: ({ data: data2 }: { data: Object }) => {
                if (!data2 || !data2.entity) return null;
                // El subtype puede venir vacío para productos con el tipo mal informado
                // en BD (p.ej. el plan N5522). Como el id ya resolvió contra /collectives
                // sabemos que es un colectivo, así que por defecto lo tratamos como 'fund'
                // para no descartar el producto (si no, la posición se quedaría sin fondo
                // y la cartera mostraría solo el ISIN).
                const itemProductType = getProductTypeById(data2.subtype) || getProductTypeById('fund');
                return models.investmentProducts.product(itemProductType, data2.entity);
              },
              onError: () => null
            });
          },
          onError: () => null
        }))
    ).then((response) => {
      // Dedupe by canonical identifier — a URL can address the same fund through different
      // tokens (e.g. `?products=ISIN_A,IdSecurity_A`), and `fetchFunds` would otherwise put
      // the same fund into the store twice and render two identical columns with the same
      // React key.
      const seen = new Set();
      const dedupedFunds = response.filter((fund) => {
        if (!fund) return false;
        const key = getProductIdentifier(fund);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      dispatch(setFunds(dedupedFunds));
      return dedupedFunds;
    });
}

export function openDialog() {
  return {
    type: 'COMPARISON_OPEN_DIALOG',
    index: 1
  };
}

export function closeDialog() {
  return {
    type: 'COMPARISON_CLOSE_DIALOG',
    index: 1
  };
}

export function removeProduct(product: Object) {
  return {
    type: 'COMPARISON_REMOVE_PRODUCT',
    product
  };
}

export function setUserComparisons(sessions: Array<Object>) {
  return {
    type: 'COMPARISON_SET_USER_COMPARISONS',
    sessions
  };
}

export function setUserComparisonsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'COMPARISON_SET_USER_COMPARISONS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setUserComparisonsDialog(open: boolean) {
  return {
    type: 'COMPARISON_SET_USER_COMPARISONS_DIALOG',
    open
  };
}

// thunks
export function fetchUserComparisons(cookies?: string) {
  return (dispatch: Dispatch) =>
    Promise.all([dispatch(setUserComparisonsFetchingStatus(true, true))]).then(() =>
      finectAPI({
        version: 'v4',
        path: `applications/products-comparison/sessions`,
        method: 'GET',
        credentials: cookies,
        onSuccess: ({ data }: { data: Array<Object> }) =>
          dispatch(
            setUserComparisons(
              data.sort((sessionA, sessionB) => new Date(sessionB.created) - new Date(sessionA.created))
            )
          ),
        // Sin esto, un fallo (visitante anónimo -> 401, o error de red) dejaba `fetching` en true
        // PARA SIEMPRE: nadie volvía a bajarlo, y las vistas que esperan a que termine la carga
        // —el comparador con ?saved= en la URL— se quedaban en el esqueleto indefinidamente.
        onError: () => dispatch(setUserComparisons([]))
      }));
}

// Initial Data
export function fetchInitialData(cookies?: string, productType: Object | null, funds: Array<string>) {
  return (dispatch: Dispatch) => {
    const promises = [dispatch(fetchUserComparisons(cookies))];
    funds.length && promises.push(dispatch(fetchFunds(productType, funds)));
    promises.push(dispatch(fetchSetting('sonia-in-comparison')));

    return Promise.all(promises);
  };
}

// Products

export function setProducts(products: Array<Object>, concat: boolean = false) {
  return {
    type: 'COMPARISON_SET_PRODUCTS',
    products,
    concat
  };
}

// prettier-ignore
export function fetchProducts(productType: Object, productsIds: Array<string>, concat: boolean = false) {
  const family = productType.category === 'iic' ? 'collectives' : 'generics';

  const expand = expands[family] || null;

  return (dispatch: Dispatch) => {
    const promises = productsIds.map(id =>
      finectAPI({
        version: 'v4',
        path: `products/${family}/${productType.api}/${id}${expand ? `?expand=${expand}` : ''}`,
        method: 'GET',
        onSuccess: ({ data }: { data: Object }) => data,
        onError: () => null
      }));

    return Promise.all(promises).then(response =>
      dispatch(setProducts(response.map(product => ({ subtype: productType.id, entity: product })), concat)));
  };
}

// **************************************************************************************************************+
// Drawer comparison

export function setDrawerProductSelected(product: Object, plus?: boolean) {
  return {
    type: 'DRAWER_COMPARISON_SET_PRODUCT',
    product,
    plus
  };
}

export function setDrawerEmpty() {
  return {
    type: 'DRAWER_COMPARISON_SET_EMPTY'
  };
}

/**
 * Análisis
 *
 */
export function setAnalysis(analysis: string) {
  return {
    type: 'SET_ANALYSIS',
    analysis
  };
}

export function setAnalysisError(analysisError: string) {
  return {
    type: 'SET_ANALYSIS_ERROR',
    analysisError
  };
}

export function setLoadingAnalysis(loadingAnalysis: boolean) {
  return {
    type: 'SET_LOADING_ANALYSIS',
    loadingAnalysis
  };
}

// prettier-ignore
export function createAnalysis(uuid: string, funds: Array<Object>, type: string, productType: Object | null) {
  const app = productType && productType.api ? `${productType.api}-comparison` : 'supercomparison';
  return (dispatch: Dispatch) => {
    dispatch(setLoadingAnalysis(true));
    finectAPI({
      version: 'v4',
      path: 'products/analysis/comparison',
      method: 'POST',
      body: {
        type,
        funds,
        app
      },
      onSuccess: ({ data }: { data: Object }) => {
        dispatch(setAnalysis(`${data}
<i>Este análisis se ha elaborado con inteligencia artificial, a partir de la información disponible en las fichas de Finect. No ha sido revisado por el equipo de Finect y no constituye una recomendación ni propuesta de inversión. La inversión contiene riesgos. Las rentabilidades pasadas no son garantía de rentabilidades futuras.</i>
<b>¿Te puedo ayudar con algo más?</b>`));
        dispatch(setLoadingAnalysis(false));
      },
      onError: (error) => {
        console.log(error.status);
        if (error.status === 429) {
          dispatch(setAnalysisError('Ya has hecho trabajar mucho a SonIA por hoy 😉 Hasta la IA se cansa, vuelve mañana.'));
        } else if (error.status === 503) {
          dispatch(setAnalysisError('Hemos tenido muchísimas peticiones hoy. La IA tiene que descansar, vuelve mañana :)'));
        } else {
          dispatch(setAnalysisError('Parece que ha habido un error al generar el análsisis. Por favor, inténtalo de nuevo.\n<b>¿Te puedo ayudar con algo más?</b>'));
        }
        dispatch(setLoadingAnalysis(false));
      }
    });
  };
}
